import { useSeo } from '../lib/seo';
/**
 * Public /awards page. Status-aware: nominations form / ballot / winners showcase.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, MapPin, Sparkles, Ticket, Trophy, Vote } from 'lucide-react';
import api from '../lib/api';
import { track } from '../lib/tracker';
import finalistLogo from '../assets/finalist-2026-logo.jpg';

export default function AwardsPage() {
  useSeo(
    'Leicestershire Tourism Awards — Vote Now | Go Leicestershire',
    'Celebrate the best of Leicestershire tourism — browse the nominees and cast your vote in the Leicestershire Tourism Awards.'
  );
  const [cycle, setCycle] = useState(null);
  const [allCycles, setAllCycles] = useState([]);
  const [finalistCats, setFinalistCats] = useState([]);

  useEffect(() => {
    api.get('/awards/current').then((r) => setCycle(r.data || {}));
    api.get('/awards/cycles').then((r) => setAllCycles(r.data.items.filter((c) => c.status === 'published')));
    api.get('/awards-finalists').then((r) => setFinalistCats(r.data.categories || [])).catch(() => {});
  }, []);

  if (!cycle) return <div className="py-32 text-center text-muted">Loading...</div>;

  // No active cycle
  if (!cycle.id) {
    return (
      <div className="max-w-[1100px] mx-auto px-6 py-32 text-center">
        <Trophy size={48} className="mx-auto text-mustard mb-6"/>
        <h1 className="font-display text-5xl mb-4">Tourism Awards</h1>
        <p className="text-muted text-lg max-w-xl mx-auto">The 2026 Leicestershire Tourism Awards will be launching soon. Check back, or sign up for the newsletter to be the first to know when nominations open.</p>
      </div>
    );
  }

  const status = cycle.status;
  const isNoms = status === 'nominations_open';
  const isVoting = status === 'voting_open';
  const isClosed = status === 'voting_closed' || status === 'published';

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-charcoal text-base py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40"/>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-mustard/10 rounded-full blur-3xl"/>
        <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-heritage/30 rounded-full blur-3xl"/>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-5 inline-flex items-center gap-2"><Trophy size={14}/> {cycle.year} · Tourism Awards</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.92] mb-6 max-w-3xl">{cycle.name}</h1>
          <p className="text-lg md:text-xl text-base/85 max-w-2xl mb-8 leading-relaxed">{cycle.description || "Celebrating the people and places that make Leicestershire one of England's best-kept secrets."}</p>
          <div className="flex flex-wrap items-center gap-3">
            {isNoms && <span className="inline-flex items-center gap-2 bg-mustard text-charcoal px-5 py-3 rounded-full font-semibold"><Sparkles size={16}/> Nominations open</span>}
            {isVoting && (
              <Link to="/awards/vote" data-testid="awards-hero-vote-link"
                className="inline-flex items-center gap-2 bg-mustard text-charcoal px-5 py-3 rounded-full font-semibold hover:bg-base transition-colors">
                <Vote size={16}/> Voting is now open — vote here
              </Link>
            )}
            {isClosed && (
              <Link to="/awards/vote" data-testid="awards-hero-results-link"
                className="inline-flex items-center gap-2 bg-heritage text-base px-5 py-3 rounded-full font-semibold hover:bg-mustard hover:text-charcoal transition-colors">
                <Award size={16}/> Public vote results
              </Link>
            )}
            <a href="/awards/tickets" data-testid="awards-hero-book-tickets"
              className="inline-flex items-center gap-2 bg-heritage text-base px-5 py-3 rounded-full font-semibold hover:bg-mustard hover:text-charcoal transition-colors">
              <Ticket size={16}/> Book ceremony tickets
            </a>
            <a href="/awards/criteria" data-testid="awards-hero-criteria"
              className="inline-flex items-center gap-2 border border-base/40 text-base px-5 py-3 rounded-full font-semibold hover:border-base transition-colors">
              Category criteria
            </a>
            <span className="text-base/60 text-sm">{(cycle.categories || []).length} categories</span>
          </div>
        </div>
      </section>

      {/* PUBLIC VOTE CTA — the people's-choice ballot lives on its own page so
          voters don't have to scroll past every finalist category to find it. */}
      {(isVoting || isClosed) && (
        <section className="bg-mustard" data-testid="awards-vote-cta">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-10 md:py-14 flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="label text-charcoal/70 mb-2 inline-flex items-center gap-2"><Vote size={13}/> {isVoting ? 'Public vote — open now' : 'Public vote'}</div>
              <h2 className="font-display text-2xl md:text-4xl text-charcoal leading-tight">
                {(cycle.categories || []).length > 0 ? (cycle.categories || []).map((c) => c.name).join(' · ') : 'The public vote'}
                {isVoting ? ' — you decide.' : ' — results.'}
              </h2>
              <p className="text-charcoal/75 mt-2 text-sm md:text-base">
                {isVoting
                  ? 'This award is chosen by the public, not the judges. See the entries and cast your vote on the dedicated voting page.'
                  : 'Voting has closed — see the entries and how it finished.'}
              </p>
            </div>
            <Link to="/awards/vote" data-testid="awards-vote-cta-btn"
              className="inline-flex items-center gap-2 bg-charcoal text-cream px-7 py-4 rounded-full font-semibold text-base md:text-lg hover:bg-heritage transition-colors shrink-0">
              {isVoting ? 'Vote now' : 'See the results'} <ArrowRight size={18}/>
            </Link>
          </div>
        </section>
      )}

      {/* 2026 FINALISTS — populated from the admin spreadsheet import */}
      {finalistCats.length > 0 && (
        <section id="finalists" className="py-16 md:py-24 bg-cream scroll-mt-24" data-testid="awards-finalists-section">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <div className="label text-heritage mb-3 inline-flex items-center gap-2"><Trophy size={13}/> The shortlist</div>
            <h2 className="font-display text-3xl md:text-5xl mb-4">Meet the 2026 finalists</h2>
            <p className="text-muted max-w-2xl mb-10 md:mb-14">Congratulations to every business shortlisted for this year's Leicestershire Tourism &amp; Hospitality Awards. Winners are revealed at the ceremony in November.</p>
            {finalistCats.map((cat) => (
              <div key={cat.name} className="mb-12 md:mb-16" data-testid={`finalists-category-${cat.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <h3 className="font-display text-xl md:text-2xl mb-5 flex items-center gap-2.5">
                  <span className="w-8 h-[2px] bg-mustard inline-block"/>{cat.name}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {cat.finalists.map((f) => {
                    const inner = (
                      <>
                        <div className="relative aspect-[4/3] overflow-hidden bg-charcoal/5">
                          {f.hero_image ? (
                            <img src={f.hero_image} alt={f.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                          ) : (
                            <img src={finalistLogo} alt={`${f.name} — 2026 Tourism & Hospitality Awards Finalist`} loading="lazy"
                              className="w-full h-full object-cover" data-testid={`finalist-logo-placeholder-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}/>
                          )}
                          <span className="absolute top-2.5 left-2.5 bg-mustard text-charcoal text-[10px] leading-none font-bold px-2 py-1 rounded-full">2026 Finalist</span>
                        </div>
                        <div className="p-3.5">
                          <div className="font-display text-sm md:text-base leading-tight group-hover:text-heritage transition-colors">{f.name}</div>
                          {f.town && <div className="text-xs text-muted mt-1 inline-flex items-center gap-1"><MapPin size={10}/>{f.town}</div>}
                        </div>
                      </>
                    );
                    const cls = "group bg-white rounded-2xl overflow-hidden border border-charcoal/10 block transition-all hover:shadow-soft hover:-translate-y-0.5";
                    return f.href ? (
                      <Link key={f.name} to={f.href} data-testid={`finalist-tile-${f.slug}`} className={cls}>{inner}</Link>
                    ) : (
                      <div key={f.name} data-testid={`finalist-tile-pending-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={cls}>{inner}</div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* NOMINATIONS */}
      {isNoms && (
        <section className="py-20">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12">
            <div className="label text-heritage mb-3">Nominate a favourite</div>
            <h2 className="font-display text-4xl mb-3">Who should win this year?</h2>
            <p className="text-muted text-lg mb-10">Pick a category and submit your nominee. We'll review each entry and approved nominees will go forward to public voting.</p>
            {cycle.categories.map((c) => (
              <NominationForm key={c.id} category={c} cycleId={cycle.id}/>
            ))}
          </div>
        </section>
      )}

      {/* Voting ballot + winners now live on /awards/vote (linked from the CTA above) */}

      {/* PAST WINNERS */}
      {allCycles.length > 0 && (
        <section className="py-16 border-t border-charcoal/10">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <h2 className="font-display text-3xl mb-8">Past awards</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {allCycles.map((c) => (
                <Link key={c.id} to={`/awards/${c.year}`} data-testid={`past-cycle-${c.year}`} className="group bg-white border border-charcoal/10 rounded-3xl p-8 hover:border-mustard hover:-translate-y-1 transition-all">
                  <Trophy size={28} className="text-mustard mb-4"/>
                  <div className="font-display text-2xl mb-2">{c.year}</div>
                  <div className="text-muted text-sm">{c.name}</div>
                  <div className="mt-4 text-heritage font-semibold inline-flex items-center gap-1 text-sm">View winners <ArrowRight size={14}/></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export function NominationForm({ category, cycleId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', town: '', blurb: '', website: '', submitted_by_name: '', submitted_by_email: '' });
  const [status, setStatus] = useState('idle');
  const [serverMsg, setServerMsg] = useState('');

  async function submit(e) {
    e.preventDefault();
    setStatus('loading');
    try {
      const { data } = await api.post(`/awards/cycles/${cycleId}/nominate`, { ...form, category_id: category.id });
      track.awardNomination(category.id);
      setServerMsg(data?.duplicate ? (data.message || '') : '');
      setStatus(data?.duplicate ? 'duplicate' : 'done');
      setForm({ name: '', town: '', blurb: '', website: '', submitted_by_name: '', submitted_by_email: '' });
    } catch (e) { setStatus('error'); }
  }

  return (
    <div className="bg-white border border-charcoal/10 rounded-3xl p-6 mb-4" data-testid={`nominate-category-${category.slug}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left">
        <div>
          <h3 className="font-display text-2xl">{category.name}</h3>
          {category.description && <p className="text-muted text-sm mt-1">{category.description}</p>}
        </div>
        <span className="text-heritage font-semibold text-sm shrink-0">{open ? 'Close' : 'Nominate →'}</span>
      </button>
      {open && (
        <form onSubmit={submit} className="mt-6 pt-6 border-t border-charcoal/10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Name of venue / business *" className="md:col-span-2 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid={`nominate-name-${category.slug}`}/>
          <input value={form.town} onChange={(e) => setForm({...form, town: e.target.value})} placeholder="Town" className="bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} placeholder="Website (optional)" className="bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <textarea rows={2} value={form.blurb} onChange={(e) => setForm({...form, blurb: e.target.value})} placeholder="Why do they deserve it?" className="md:col-span-2 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <input value={form.submitted_by_name} onChange={(e) => setForm({...form, submitted_by_name: e.target.value})} placeholder="Your name (optional)" className="bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <input type="email" value={form.submitted_by_email} onChange={(e) => setForm({...form, submitted_by_email: e.target.value})} placeholder="Your email (optional)" className="bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <div className="md:col-span-2 flex items-center gap-3">
            <button type="submit" disabled={status === 'loading'} data-testid={`nominate-submit-${category.slug}`} className="bg-heritage text-base px-6 py-3 rounded-full font-semibold disabled:opacity-60">
              {status === 'loading' ? 'Sending...' : status === 'done' ? 'Thanks — pending review' : 'Submit nomination'}
            </button>
            {status === 'done' && <span className="text-forest text-sm">Your nomination is in. We'll review it and approved entries will appear on the public ballot.</span>}
            {status === 'duplicate' && <span className="text-forest text-sm font-semibold" data-testid={`nominate-duplicate-${category.slug}`}>{serverMsg}</span>}
            {status === 'error' && <span className="text-heritage text-sm">Something went wrong. Try again.</span>}
          </div>
        </form>
      )}
    </div>
  );
}
